import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { useAuth } from "@clerk/clerk-react";

/**
 * Crea una función que obtiene el token de autenticación de Clerk para usarlo en las solicitudes
 * @returns {Object} Objeto con el token de autenticación si está disponible
 */
export const prepareClerkHeaders = () => {
  // Esta función debe ejecutarse en tiempo de ejecución para obtener el token actual
  // No podemos usar hooks como useAuth aquí directamente
  
  // First try to get values from window globals
  let token = (window as any).__clerk_jwt;
  let userId = (window as any).__clerk_user_id;
  let userEmail = (window as any).__clerk_user_email;
  
  // If not found in window, try localStorage as a fallback
  if (!token) {
    token = localStorage.getItem('clerk-token');
  }
  
  if (!userId) {
    userId = localStorage.getItem('clerk-user-id');
  }
  
  if (!userEmail) {
    userEmail = localStorage.getItem('clerk-user-email');
  }
  
  // Debug log in development environment
  if (process.env.NODE_ENV !== 'production') {
    console.log('[API] Auth headers prepared', {
      hasToken: !!token,
      hasUserId: !!userId,
      tokenSource: token ? ((window as any).__clerk_jwt ? 'window' : 'localStorage') : 'none',
      userIdSource: userId ? ((window as any).__clerk_user_id ? 'window' : 'localStorage') : 'none'
    });
  }
  
  if (token && userId) {
    return {
      Authorization: `Bearer ${token}`,
      'x-clerk-user-id': userId,
      'x-clerk-email': userEmail || '',
    };
  }
  
  return {};
};

// Configuración base para las solicitudes a la API con manejo de errores incorporado
export const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_URL || 'http://localhost:8080',
  prepareHeaders: (headers) => {
    // Asegurar que siempre se acepte JSON en las respuestas
    headers.set("Accept", "application/json");
    
    // Agregar headers de Clerk si están disponibles
    const clerkHeaders = prepareClerkHeaders();
    Object.entries(clerkHeaders).forEach(([key, value]) => {
      headers.set(key, value);
    });
    
    // Debug log in development environment
    if (process.env.NODE_ENV !== 'production') {
      console.log('[API] Request headers prepared', {
        hasAuth: headers.has('Authorization'),
        hasUserId: headers.has('x-clerk-user-id'),
        hasEmail: headers.has('x-clerk-email')
      });
    }
    
    return headers;
  },
  credentials: "include", // Incluir cookies en las solicitudes
});

/**
 * Función auxiliar para establecer el token y el usuario de Clerk globalmente
 * Debe llamarse después de que el usuario inicie sesión con Clerk
 */
export const setClerkAuthInfo = (token: string, userId: string, userEmail?: string) => {
  (window as any).__clerk_jwt = token;
  (window as any).__clerk_user_id = userId;
  (window as any).__clerk_user_email = userEmail || '';
  
  // Also save to localStorage for components that use it from there
  localStorage.setItem('clerk-token', token);
  localStorage.setItem('clerk-user-id', userId);
  localStorage.setItem('clerk-user-email', userEmail || '');
  
  // Log when this happens in development to help with debugging
  if (process.env.NODE_ENV !== 'production') {
    console.log('[Auth] Set Clerk auth info', { userId, hasEmail: !!userEmail });
  }
};

/**
 * Función auxiliar para limpiar el token y el usuario de Clerk globalmente
 * Debe llamarse después de que el usuario cierre sesión con Clerk
 */
export const clearClerkAuthInfo = () => {
  (window as any).__clerk_jwt = null;
  (window as any).__clerk_user_id = null;
  (window as any).__clerk_user_email = null;
  
  // Also clear from localStorage
  localStorage.removeItem('clerk-token');
  localStorage.removeItem('clerk-user-id');
  localStorage.removeItem('clerk-user-email');
};
